import React, { useState, useEffect, useCallback } from 'react';
import { Table, Select, DatePicker, Button, Tag, Tooltip, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getAccessLogs } from '../../api/logs';
import UserSearchSelect from '../common/UserSearchSelect';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import useServerSort from '../../hooks/useServerSort';

const { Option } = Select;
const { RangePicker } = DatePicker;

const ACTION_COLORS = {
  login:          'green',
  logout:         'default',
  login_failed:   'red',
  token_refresh:  'blue',
  impersonate:    'orange',
};

// Backend sadece login/logout icin action alanini dolduruyor, geri kalan
// (buyuk cogunluk) istek icin bu alan hep bos geliyordu ve tabloda hep "---"
// gorunuyordu. Action bos oldugunda HTTP metodundan genel bir islem adi
// turetiliyor - boylece hicbir satir bos kalmiyor.
const METHOD_ACTION_LABELS = {
  GET:    'Görüntüleme',
  POST:   'Oluşturma',
  PUT:    'Güncelleme',
  PATCH:  'Güncelleme',
  DELETE: 'Silme',
};

const METHOD_COLORS = {
  GET:    'blue',
  POST:   'green',
  PUT:    'orange',
  PATCH:  'orange',
  DELETE: 'red',
};

// API path'inin ilk segmentini (ör. /api/students/42 -> "students") kullanıcının
// anlayacağı sayfa adına eşliyor. Eşleşme yoksa segment kendisi başlık
// biçiminde gösteriliyor - böylece yeni bir uç eklendiğinde liste güncellenmeyi
// unutulsa bile "boş" değil, en azından okunabilir bir isim çıkıyor.
const RESOURCE_LABELS = {
  'access-logs':        'Erişim Logları',
  'admin':              'Admin İşlemi',
  'agenda-items':       'Ajanda',
  'announcements':      'Duyurular',
  'attendances':        'Yoklama',
  'audit-logs':         'Denetim Logları',
  'backups':            'Yedekleme',
  'branches':           'Şubeler',
  'certificates':       'Sertifikalar',
  'change-password':    'Şifre Değiştir',
  'chat':               'Sohbet / AI Asistan',
  'classrooms':         'Derslikler',
  'commands':           'Sistem Komutları',
  'courses':            'Sertifika Programları',
  'daily-notes':        'Günlük Notlar',
  'delegations':        'Vekalet',
  'discount-campaigns': 'Kampanyalar',
  'discounts':          'İndirimler',
  'enrollments':        'Kayıtlar',
  'google-drive':       'Google Drive',
  'intern':             'Stajyer Paneli',
  'kvkk':               'Onay Metinleri',
  'leaves':             'İzin Talepleri',
  'lesson-sessions':    'Dersler',
  'logout':             'Çıkış',
  'makeup-rights':      'Telafi Hakları',
  'me':                 'Profil',
  'notification-templates': 'Bildirim Şablonları',
  'notifications':      'Bildirimler',
  'parent':             'Veli Paneli',
  'parent-requests':    'Veli Talepleri',
  'payments':           'Ödemeler',
  'personnel-files':    'Özlük Dosyaları',
  'push-token':         'Push Token',
  'refresh':            'Oturum Yenileme',
  'reports':            'Raporlar',
  'schedule':           'Program / Takvim',
  'settings':           'Ayarlar',
  'students':           'Öğrenciler',
  'terms':              'Dönemler',
  'users':              'Kullanıcılar',
  'work-tasks':         'İş Takip',
};

const parseRequestPath = (rawUrl) => {
  if (!rawUrl) return null;
  let path = rawUrl;
  try {
    path = new URL(rawUrl).pathname;
  } catch {
    // zaten path ise (mutlak URL degilse) oldugu gibi kullan
  }
  const segments = path.replace(/^\/?api\//, '').split('/').filter(Boolean);
  const resource = segments[0] || null;
  const label = resource
    ? (RESOURCE_LABELS[resource] || resource.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    : null;
  return { label, fullPath: '/' + segments.join('/') };
};

const AccessLogTab = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('accessLogs', 20);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();

  const [filters, setFilters] = useState({
    user_id:   null,
    action:    null,
    date_from: null,
    date_to:   null,
  });

  useEffect(() => {
    fetchLogs(1);
  }, []);

  const fetchLogs = useCallback(async (page = 1, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const res = await getAccessLogs({
        ...filters,
        page,
        per_page: perPage,
        sort_by: sortBy || undefined,
        sort_direction: sortDir || undefined,
      });
      setLogs(res.data.data || []);
      setPagination(p => ({ ...p, current: page, pageSize: perPage, total: res.data.total }));
    } catch {
      message.error('Erişim logları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize, sortField, sortOrder]);

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'created_at',
      width: 150,
      sorter: true,
      sortOrder: sortField === 'created_at' ? sortOrder : null,
      render: v => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Kullanıcı',
      key: 'user',
      render: (_, r) => r.user ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${r.user.id}`)}>
          {r.user.name} {r.user.surname}
        </Button>
      ) : '—',
    },
    {
      title: 'İşlem',
      dataIndex: 'action',
      width: 140,
      sorter: true,
      sortOrder: sortField === 'action' ? sortOrder : null,
      render: (v, r) => {
        if (v) {
          return (
            <Tag color={ACTION_COLORS[v] || 'default'}>
              {v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </Tag>
          );
        }
        const derived = METHOD_ACTION_LABELS[r.method];
        return derived
          ? <Tag color={METHOD_COLORS[r.method] || 'default'}>{derived}</Tag>
          : <span className="text-gray-400">—</span>;
      },
    },
    {
      title: 'Sayfa / Endpoint',
      key: 'endpoint',
      width: 220,
      render: (_, r) => {
        const parsed = parseRequestPath(r.url);
        if (!parsed?.label) return <span className="text-gray-400">—</span>;
        return (
          <Tooltip title={`${r.method || ''} ${parsed.fullPath}`}>
            <span className="cursor-default">
              {r.method && (
                <Tag color={METHOD_COLORS[r.method] || 'default'} style={{ marginRight: 6 }}>
                  {r.method}
                </Tag>
              )}
              {parsed.label}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'IP Adresi',
      dataIndex: 'ip_address',
      width: 130,
      render: v => v || '—',
    },
    {
      title: 'Tarayıcı / Cihaz',
      dataIndex: 'user_agent',
      render: v => v ? (
        <span className="text-xs text-gray-500 truncate block max-w-xs" title={v}>
          {v.length > 60 ? v.substring(0, 60) + '…' : v}
        </span>
      ) : '—',
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        {/*
          SUNUCU TARAFLI ARAMA — bkz. AuditLogTab'daki aynı not. Erişim
          loglarında bu daha da kritik: bir güvenlik uyarısı araştırılırken
          bakılacak kişi çoğunlukla VELİ ve veliler ilk 100 kaydın dışında
          kalıyordu.
        */}
        <UserSearchSelect
          allowClear
          style={{ width: 220 }}
          placeholder="Kullanıcı ara..."
          value={filters.user_id}
          onChange={v => setFilters(f => ({ ...f, user_id: v }))}
        />

        <Select
          allowClear
          placeholder="İşlem"
          style={{ width: 160 }}
          value={filters.action}
          onChange={v => setFilters(f => ({ ...f, action: v }))}
        >
          {Object.keys(ACTION_COLORS).map(k => (
            <Option key={k} value={k}>
              {k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </Option>
          ))}
        </Select>

        <RangePicker
          format="DD.MM.YYYY"
          onChange={(_, strs) => setFilters(f => ({
            ...f,
            date_from: strs[0] ? dayjs(strs[0], 'DD.MM.YYYY').format('YYYY-MM-DD') : null,
            date_to:   strs[1] ? dayjs(strs[1], 'DD.MM.YYYY').format('YYYY-MM-DD') : null,
          }))}
        />

        <Button type="primary" onClick={() => fetchLogs(1)}>Filtrele</Button>
      </div>

      <Table
        dataSource={logs}
        rowKey="id"
        columns={columns}
        loading={loading}
        size="small"
        onChange={(paginationInfo, _filters, sorter) => {
          const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
          setPersistedPageSize(paginationInfo.pageSize);
          fetchLogs(paginationInfo.current, paginationInfo.pageSize, sField, sDir);
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: total => `Toplam ${total} kayıt`,
        }}
      />
    </div>
  );
};

export default AccessLogTab;
